import type { CustomField, FormCoreTarget, FormQuestion, FormQuestionType } from "@astrum/shared";

/*
 * Pure helpers for intake forms: which question type a target takes, and how a
 * target is named. The server re-checks every definition; this keeps the
 * builder from offering combinations it will refuse.
 */

export const CORE_TARGET_LABELS: Record<FormCoreTarget, string> = {
  title: "Task title",
  description: "Task description",
  dueAt: "Due date",
  assignee: "Assignee",
};

const CUSTOM_TYPES: Record<CustomField["type"], FormQuestionType[]> = {
  text: ["short_text", "long_text"],
  number: ["number"],
  date: ["date"],
  dropdown: ["dropdown"],
  person: ["person"],
};

const CORE_TYPES: Record<FormCoreTarget, FormQuestionType[]> = {
  title: ["short_text"],
  description: ["short_text", "long_text"],
  dueAt: ["date"],
  assignee: ["person"],
};

export const QUESTION_TYPE_LABELS: Record<FormQuestionType, string> = {
  short_text: "Short text",
  long_text: "Long text",
  number: "Number",
  date: "Date",
  dropdown: "Dropdown",
  person: "Person",
};

/** The question types a target accepts, the first being the default. */
export function typesForTarget(target: FormQuestion["target"], fields: CustomField[]): FormQuestionType[] {
  if (target.kind === "core") return CORE_TYPES[target.field];
  const field = fields.find((f) => f.id === target.fieldId);
  return field ? CUSTOM_TYPES[field.type] : [];
}

/** A stable key for a target, for use as a select value. */
export const targetKey = (t: FormQuestion["target"]) => (t.kind === "core" ? `core:${t.field}` : `custom:${t.fieldId}`);

export function targetFromKey(key: string): FormQuestion["target"] {
  const [kind, rest = ""] = key.split(/:(.*)/s);
  return kind === "core" ? { kind: "core", field: rest as FormCoreTarget } : { kind: "custom", fieldId: rest };
}

export const newQuestionId = () => `q${Math.random().toString(36).slice(2, 8)}${Date.now().toString(36).slice(-3)}`;

export const formUrl = (formId: string) => `${window.location.origin}/forms/${formId}`;
